import React, { useState } from 'react';

import Icon from 'components/AppIcon';

const BookingSummaryPanel = ({ formData, isVisible = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getRoomDetails = () => {
    const roomTypes = {
      'deluxe-forest-villa': { name: 'Deluxe Forest Villa', price: 4500 },
      'river-view-cottage': { name: 'River View Cottage', price: 3800 },
      'luxury-tent': { name: 'Luxury Tent', price: 2900 },
      'tree-house': { name: 'Tree House', price: 5200 }
    };
    return roomTypes?.[formData?.roomType] || { name: 'Select Room', price: 0 };
  };

  const getActivityDetails = () => {
    const activities = formData?.activities || [];
    const activityPrices = {
      'rafting': { name: 'River Rafting', price: 1200 },
      'safari': { name: 'Jungle Safari', price: 800 },
      'kayaking': { name: 'Kayaking', price: 900 },
      'zipline': { name: 'Zipline Adventure', price: 1500 },
      'trek': { name: 'Nature Trek', price: 600 },
      'birdwatching': { name: 'Bird Watching', price: 700 }
    };
    
    return activities?.map(activity => activityPrices?.[activity])?.filter(Boolean);
  };

  const calculateNights = () => {
    if (formData?.checkIn && formData?.checkOut) {
      const checkIn = new Date(formData.checkIn);
      const checkOut = new Date(formData.checkOut);
      const diffTime = Math.abs(checkOut - checkIn);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const calculateSubtotal = () => {
    const room = getRoomDetails();
    const nights = calculateNights();
    const roomTotal = room?.price * nights;
    
    const activities = getActivityDetails();
    // Calculate activity total based on adults and children
    const adults = formData?.guests || 2; // Default to 2 adults if guestDetails not available
    const children = 0; // Default to 0 children if guestDetails not available
    const childDiscount = 0.5; // 50% discount for children
    
    const activityTotal = activities?.reduce((sum, activity) => {
      const adultCost = activity?.price * adults;
      const childCost = activity?.price * children * childDiscount;
      return sum + adultCost + childCost;
    }, 0);
    
    return roomTotal + activityTotal;
  };

  const calculateTaxes = () => {
    return Math.round(calculateSubtotal() * 0.12); // 12% GST
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxes();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Select date';
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Desktop Sticky Panel */}
      <div className="hidden lg:block fixed right-6 top-24 w-80 bg-card border border-border rounded-lg shadow-organic-xl z-30 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">
            Booking Summary
          </h3>

          {/* Accommodation Details */}
          {formData?.roomType && (
            <div className="space-y-3 mb-4">
              <div className="flex items-start space-x-3">
                <Icon name="Bed" size={16} className="text-primary mt-1" />
                <div className="flex-1">
                  <p className="font-medium text-card-foreground">{getRoomDetails()?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formData?.guestDetails?.adults || 0} adult{(formData?.guestDetails?.adults || 0) > 1 ? 's' : ''}
                    {(formData?.guestDetails?.children > 0) && `, ${formData?.guestDetails?.children} child${(formData?.guestDetails?.children || 0) > 1 ? 'ren' : ''}`} • 
                    {calculateNights()} night{calculateNights() > 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {formData?.checkIn && formData?.checkOut && (
                <div className="flex items-center space-x-3">
                  <Icon name="Calendar" size={16} className="text-primary" />
                  <div className="text-sm">
                    <span className="text-card-foreground font-mono">
                      {formatDate(formData?.checkIn)} - {formatDate(formData?.checkOut)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price Breakdown */}
          {formData?.roomType && calculateNights() > 0 && (
            <div className="border-t border-border pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Room ({calculateNights()} night{calculateNights() > 1 ? 's' : ''})
                </span>
                <span className="font-mono text-card-foreground">
                  {formatCurrency(getRoomDetails()?.price * calculateNights())}
                </span>
              </div>

              {getActivityDetails()?.map((activity, index) => {
                const adults = formData?.guestDetails?.adults || 1;
                const children = formData?.guestDetails?.children || 0;
                const childDiscount = 0.5; // 50% discount for children
                
                const adultCost = activity?.price * adults;
                const childCost = activity?.price * children * childDiscount;
                const totalCost = adultCost + childCost;
                
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {activity?.name}
                      </span>
                      <span className="font-mono text-card-foreground">
                        {formatCurrency(totalCost)}
                      </span>
                    </div>
                    {adults > 0 && (
                      <div className="flex justify-between text-xs pl-4">
                        <span className="text-muted-foreground">
                          Adults ({adults}x @ {formatCurrency(activity?.price)})
                        </span>
                        <span className="font-mono text-muted-foreground">
                          {formatCurrency(adultCost)}
                        </span>
                      </div>
                    )}
                    {children > 0 && (
                      <div className="flex justify-between text-xs pl-4">
                        <span className="text-muted-foreground">
                          Children ({children}x @ {formatCurrency(activity?.price * childDiscount)})
                        </span>
                        <span className="font-mono text-muted-foreground">
                          {formatCurrency(childCost)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Taxes & Fees (12%)</span>
                <span className="font-mono text-card-foreground">
                  {formatCurrency(calculateTaxes())}
                </span>
              </div>

              <div className="border-t border-border pt-2 flex justify-between font-semibold">
                <span className="text-card-foreground">Total</span>
                <span className="font-mono text-primary text-lg">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>
            </div>
          )}

          {/* Guest Information */}
          {formData?.firstName && (
            <div className="border-t border-border pt-4 mt-4">
              <h4 className="font-medium text-card-foreground mb-2">Guest Details</h4>
              <p className="text-sm text-muted-foreground">
                {formData?.title} {formData?.firstName} {formData?.lastName}
              </p>
              {formData?.email && (
                <p className="text-sm text-muted-foreground">{formData?.email}</p>
              )}
              {formData?.from && (
                <p className="text-sm text-muted-foreground">From: {formData?.from}</p>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Mobile Slide-up Panel */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-50">
        {/* Collapsed State */}
        {!isExpanded && formData?.roomType && (
          <div className="bg-card border-t border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-medium text-card-foreground">{getRoomDetails()?.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(formData?.checkIn)} - {formatDate(formData?.checkOut)}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="font-mono font-semibold text-primary">
                    {formatCurrency(calculateTotal())}
                  </p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <button
                  onClick={() => setIsExpanded(true)}
                  className="p-2 rounded-md bg-primary text-primary-foreground"
                >
                  <Icon name="ChevronUp" size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded State */}
        {isExpanded && (
          <div className="bg-card border-t border-border max-h-[80vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-card-foreground">
                  Booking Summary
                </h3>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 rounded-md text-muted-foreground hover:text-card-foreground"
                >
                  <Icon name="ChevronDown" size={16} />
                </button>
              </div>

              {/* Mobile content - same as desktop */}
              {formData?.roomType && (
                <div className="space-y-3 mb-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Bed" size={16} className="text-primary mt-1" />
                    <div className="flex-1">
                      <p className="font-medium text-card-foreground">{getRoomDetails()?.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formData?.guests || 0} guest{(formData?.guests || 0) > 1 ? 's' : ''} • {calculateNights()} night{calculateNights() > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {formData?.checkIn && formData?.checkOut && (
                    <div className="flex items-center space-x-3">
                      <Icon name="Calendar" size={16} className="text-primary" />
                      <div className="text-sm">
                        <span className="text-card-foreground font-mono">
                          {formatDate(formData?.checkIn)} - {formatDate(formData?.checkOut)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {formData?.roomType && calculateNights() > 0 && (
                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Room ({calculateNights()} night{calculateNights() > 1 ? 's' : ''})
                    </span>
                    <span className="font-mono text-card-foreground">
                      {formatCurrency(getRoomDetails()?.price * calculateNights())}
                    </span>
                  </div>

                  {getActivityDetails()?.map((activity, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {activity?.name} ({formData?.guests || 1}x)
                      </span>
                      <span className="font-mono text-card-foreground">
                        {formatCurrency(activity?.price * (formData?.guests || 1))}
                      </span>
                    </div>
                  ))}

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Taxes & Fees (12%)</span>
                    <span className="font-mono text-card-foreground">
                      {formatCurrency(calculateTaxes())}
                    </span>
                  </div>

                  <div className="border-t border-border pt-2 flex justify-between font-semibold">
                    <span className="text-card-foreground">Total</span>
                    <span className="font-mono text-primary text-lg">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BookingSummaryPanel;